// Auto-follow (FFXI /follow): your character walks after a party member on its own. It presses the same
// buttons you would, so the server sees ordinary walking and prediction stays exact. Pressing any move,
// dash, jump or attack yourself stops it. A path is found over the map's tiles, so it goes around
// houses and the river instead of walking into them, and through doorways and zone edges after them.
import { SUB } from '../../../shared/constants.js';
import { IN } from '../../../shared/sim.js';
import type { ZoneMap } from '../../../shared/map.js';

const NEAR_PX = 20;          // close enough: stop and wait
const RUN_PX = 72;           // further than this: run to catch up
const REPATH_TICKS = 12;
const LOST_MS = 6000;        // out of sight this long, after reaching where they were last: give up

export interface FollowTarget { x: number; y: number; mounted: boolean }   // pixels
export type Lost = 'lost' | 'gone';

export class Follow {
  name: string | null = null;
  private path: [number, number][] = []; private pathFor = ''; private repath = 0;
  private last: { x: number; y: number } | null = null; private lostSince = 0;
  private stuck = 0; private prev = { x: 0, y: 0 }; private hopped = 0; private mountCd = 0;
  onChange: (name: string | null, why?: string) => void = () => {};

  get active(): boolean { return this.name !== null; }

  start(name: string): void { this.name = name; this.path = []; this.pathFor = ''; this.last = null; this.lostSince = 0; this.stuck = 0; this.onChange(name); }
  /** Through a door or over a zone edge: where they were last seen meant the old map. */
  newZone(): void { this.last = null; this.lostSince = 0; this.path = []; this.pathFor = ''; }
  stop(why?: string): void { if (this.name === null) return; this.name = null; this.path = []; this.onChange(null, why); }

  /** Buttons to press this tick. `own` is what the player pressed: anything active from them stops following. */
  steer(own: number, me: { x: number; y: number; mount: boolean; flying: boolean }, target: FollowTarget | null, map: ZoneMap, now: number): number {
    if (this.name === null) return own;
    if (own & (IN.up | IN.down | IN.left | IN.right | IN.dash | IN.jump | IN.attack)) { this.stop(); return own; }
    const mx = me.x / SUB, my = me.y / SUB;
    let goal: { x: number; y: number } | null = null;
    if (target) { goal = { x: target.x, y: target.y }; this.last = goal; this.lostSince = 0; }
    else if (this.last) {
      // out of sight (another zone, or too far to see): go where they were last seen, then wait a little
      goal = this.last;
      if (Math.hypot(goal.x - mx, goal.y - my) < NEAR_PX) { if (!this.lostSince) this.lostSince = now; else if (now - this.lostSince > LOST_MS) { this.stop('lost'); return own; } }
    } else { if (!this.lostSince) this.lostSince = now; else if (now - this.lostSince > LOST_MS) { this.stop('lost'); } return own; }
    let bits = own & IN.mount;
    // ride when they ride (where riding is allowed), get off when they do
    if (this.mountCd > 0) this.mountCd--;
    else if (target && map.mounts && !me.flying && target.mounted !== me.mount) { bits |= IN.mount; this.mountCd = 40; }
    const d = Math.hypot(goal.x - mx, goal.y - my);
    if (d < NEAR_PX) { this.path = []; this.stuck = 0; return bits; }
    if (me.flying) return bits | dirBits(goal.x - mx, goal.y - my);   // in the air nothing is in the way
    // path over the tiles, refreshed as they move
    const ts = map.ts, key = `${Math.floor(goal.x / ts)},${Math.floor(goal.y / ts)}`;
    if (--this.repath <= 0 || key !== this.pathFor || !this.path.length) { this.path = findPath(map, mx, my, goal.x, goal.y); this.pathFor = key; this.repath = REPATH_TICKS; }
    while (this.path.length > 1) { const [px, py] = this.path[0]; if (Math.hypot(px - mx, py - my) < 4) this.path.shift(); else break; }
    const [wx, wy] = this.path.length ? this.path[0] : [goal.x, goal.y];
    bits |= dirBits(wx - mx, wy - my);
    if (d > RUN_PX) bits |= IN.run;
    // stuck on a fence or a crate: hop
    if (Math.abs(mx - this.prev.x) + Math.abs(my - this.prev.y) < 0.2) this.stuck++; else this.stuck = 0;
    this.prev = { x: mx, y: my };
    if (this.hopped > 0) this.hopped--;
    else if (this.stuck > 20) { bits |= IN.jump; this.hopped = 40; this.stuck = 0; }
    return bits;
  }
}

function dirBits(dx: number, dy: number): number {
  let b = 0; const dead = 1.5;
  if (dx > dead) b |= IN.right; else if (dx < -dead) b |= IN.left;
  if (dy > dead) b |= IN.down; else if (dy < -dead) b |= IN.up;
  return b;
}

/** Breadth-first search over open tiles. Returns waypoints (tile centres, in pixels) ending at the goal itself. */
export function findPath(map: ZoneMap, fx: number, fy: number, gx: number, gy: number): [number, number][] {
  const ts = map.ts, W = map.w, H = map.h;
  const tile = (x: number, y: number) => [Math.max(0, Math.min(W - 1, Math.floor(x / ts))), Math.max(0, Math.min(H - 1, Math.floor(y / ts)))];
  const open = (tx: number, ty: number) => map.canStandSub(Math.round((tx + 0.5) * ts * SUB), Math.round((ty + 0.75) * ts * SUB));
  const [sx, sy] = tile(fx, fy), [ex, ey] = tile(gx, gy);
  if (sx === ex && sy === ey) return [[gx, gy]];
  const prev = new Int32Array(W * H).fill(-1), start = sy * W + sx, end = ey * W + ex;
  const q = new Int32Array(W * H); let head = 0, tail = 0;
  q[tail++] = start; prev[start] = start;
  let found = false, best = start, bestD = Infinity;
  const MAX = 4000;   // plenty for a screen or two; further away we head for the closest tile reached
  while (head < tail && head < MAX) {
    const c = q[head++], cx = c % W, cy = (c / W) | 0;
    const dd = Math.abs(cx - ex) + Math.abs(cy - ey); if (dd < bestD) { bestD = dd; best = c; }
    if (c === end) { found = true; break; }
    for (const [nx, ny] of [[cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]]) {
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
      const n = ny * W + nx; if (prev[n] !== -1) continue;
      if (n !== end && !open(nx, ny)) continue;   // the goal tile counts as open (a doorway, a zone edge)
      prev[n] = c; q[tail++] = n;
    }
  }
  const out: [number, number][] = [];
  for (let c = found ? end : best; c !== start; c = prev[c]) out.push([(c % W + 0.5) * ts, ((c / W | 0) + 0.75) * ts]);
  out.reverse();
  if (found) { out.pop(); out.push([gx, gy]); }
  return out.length ? out : [[gx, gy]];
}
